import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useUpdateDeal, VerificationConflictError } from '@/hooks/use-deals';
import type { Deal } from '@/hooks/use-deals';
import type { VerificationItem } from './types';
import { verificationFingerprint } from './fingerprint';
import { VerificationSaveQueue } from './verification-save-queue';

/**
 * Autosaves the verification document for one editor instance. Once the server
 * reports the document changed elsewhere, `conflictDeal` holds the latest deal
 * and nothing more is saved until the editor reloads and calls `setBaseBody`.
 */
export function useVerificationAutosave(dealId: string) {
  const { mutateAsync: updateDeal } = useUpdateDeal();
  const [conflictDeal, setConflictDeal] = useState<Deal | null>(null);

  const sendRef = useRef(async (items: VerificationItem[], expectedHash: string) => {
    const body = JSON.stringify(items);
    try {
      await updateDeal({
        id: dealId,
        verification_document: { body },
        expected_verification_hash: expectedHash,
      });
      return { hash: await verificationFingerprint(body) };
    } catch (error) {
      if (error instanceof VerificationConflictError)
        return { conflict: error.deal };
      throw error;
    }
  });

  const [queue] = useState(() => new VerificationSaveQueue({
    send: async (items, expectedHash) => sendRef.current(items, expectedHash),
    onSaved: (notify) => {
      if (notify)
        toast.success('Verification document saved');
    },
    onConflict: (latest) => {
      setConflictDeal(latest);
      toast.error('This verification document was changed in another tab or by another user. Your latest change was not saved.');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to save verification document');
    },
  }));

  const save = useCallback((items: VerificationItem[], immediate = false): void => {
    queue.save(items, immediate);
  }, [queue]);

  /** Records the stored body the editor's items were loaded from. */
  const setBaseBody = useCallback((body: string | null | undefined): void => {
    queue.setBase(verificationFingerprint(body));
    setConflictDeal(null);
  }, [queue]);

  // Send a still-debounced save when the panel closes instead of dropping it
  useEffect(() => () => void queue.flush(), [queue]);

  return { save, setBaseBody, conflictDeal };
}
